import { useState } from "react";
import { startGoogleSignIn } from "../../lib/auth-client";
import type { ManagementSessionState } from "../../lib/manage-session";
import { posthog } from "../../lib/posthog";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";

export function ManagementGate({
  state,
  onRetry,
  onSignIn = () => startGoogleSignIn(),
  interstitial = false,
  purpose,
}: {
  state: Exclude<ManagementSessionState, { status: "ready" }>;
  onRetry: () => void;
  onSignIn?: () => Promise<void>;
  interstitial?: boolean;
  purpose?: "mac-connect" | undefined;
}) {
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signInPending, setSignInPending] = useState(false);
  const checking = state.status === "checking";
  const signingIn = state.status === "sign-in-required";
  const copy = managementGateCopy(state, purpose);
  const content = (
    <>
      <div className="step-label">Your account</div>
      <h2 id="manage-heading">{copy.heading}</h2>
      <p>{copy.description}</p>
      {signingIn && (
        <>
          <button
            className="primary-button google-sign-in"
            type="button"
            disabled={signInPending}
            onClick={() => {
              if (signInPending) return;
              setSignInError(null);
              setSignInPending(true);
              posthog.capture("sign_in_started", { provider: "google" });
              void onSignIn().catch((error: unknown) => {
                setSignInPending(false);
                setSignInError(error instanceof Error ? error.message : "Google sign-in could not start.");
              });
            }}
          >
            <span className="google-logo" aria-hidden="true">
              <svg viewBox="0 0 18 18" role="img">
                <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.909c1.702-1.567 2.683-3.874 2.683-6.614Z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.957-2.181l-2.909-2.258c-.806.54-1.835.86-3.048.86-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
                <path fill="#FBBC05" d="M3.963 10.707A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.707V4.961H.956A9 9 0 0 0 0 9c0 1.45.347 2.824.956 4.039l3.007-2.332Z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.426 0 9 0A9 9 0 0 0 .956 4.961l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58Z" />
              </svg>
            </span>
            {signInPending ? "Opening Google…" : "Continue with Google"}
          </button>
          {signInError && <p className="error-message" role="alert">{signInError}</p>}
        </>
      )}
      {checking && <span className="progress-orbit" aria-hidden="true" />}
      {state.status === "failed" && (
        <button className="secondary-button" type="button" onClick={onRetry}>Check again</button>
      )}
    </>
  );
  return (
    <section
      className={`flow-card management-gate${interstitial ? " management-gate-interstitial" : ""}`}
      data-state={state.status}
      aria-labelledby="manage-heading"
    >
      {interstitial && (
        <div className="management-gate-visual" aria-hidden="true">
          <span className="management-gate-cloud management-gate-cloud-one" />
          <span className="management-gate-cloud management-gate-cloud-two" />
          <span className="management-gate-route"><i /><i /><i /></span>
          <span className="management-gate-door-halo">
            <DoorwayBrandIcon className="management-gate-door" name="door-solid-green" />
          </span>
          <span className="management-gate-visual-label">Opening your workspace</span>
        </div>
      )}
      {interstitial ? <div className="management-gate-content">{content}</div> : content}
    </section>
  );
}

function managementGateCopy(
  state: Exclude<ManagementSessionState, { status: "ready" }>,
  purpose?: "mac-connect",
): { heading: string; description: string } {
  switch (state.status) {
    case "checking":
      return { heading: "Checking your account", description: "This takes a moment." };
    case "sign-in-required":
      if (purpose === "mac-connect") {
        return {
          heading: "Connect Doorway for Mac",
          description: "Sign in to link this Mac to your Doorway account. New users receive a Free account automatically.",
        };
      }
      return {
        heading: "Open the door to your projects",
        description: "Sign in with Google so Doorway can keep your projects, API keys, and plan attached to you.",
      };
    case "access-required":
      return {
        heading: "This project still needs an owner",
        description: `${state.user.email} is signed in. Doorway support can connect this project to your account.`,
      };
    case "failed":
      if (state.mode === "google") {
        return {
          heading: "We couldn't check your account",
          description: "Try again in a moment. If this keeps happening, contact Doorway support.",
        };
      }
      if (!state.mode) {
        return {
          heading: "We couldn't check your account",
          description: "Check your connection and try again in a moment.",
        };
      }
      return {
        heading: "Open your private management link",
        description: "This one-time link has expired or was already used. Create a fresh link from the setup command.",
      };
    case "link-required":
      return {
        heading: "Open your private management link",
        description: "The public app lives here, but publishing controls open only from the private link created during setup.",
      };
  }
}
